import { ChevronLeft } from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  FLIGHT_IN_MS,
  FLIGHT_OUT_MS,
  drop,
  frameOf,
  inFrames,
  outFrames,
  pillFrame,
} from '@/components/brand/coreFlight';
import { KanameMark, type MarkCue, type MarkLife, type MarkRhythm } from '@/components/brand/KanameMark';
import { KanameWordmark } from '@/components/brand/KanameWordmark';
import { useGust } from '@/components/brand/useGust';
import { CORE_R, MARK_HALF } from '@/components/brand/geometry';
import { useObserverAnchor } from '@/components/observer/useObserverAnchor';
import { Tooltip, cn } from '@/components/ui';
import { AccountMenu } from './AccountMenu';
import { prefersReducedMotion } from './entrance';
import { MenuLabel } from './MenuLabel';
import type { AccountInfo, MainMenuItem, NavTone } from './types';
import { useCoreHome } from './useCoreHome';
import { useSlidingBox } from './useSlidingBox';

/*
  MainMenu — главное меню, уровень 1 (docs/foundation.md). Каркас и движение основного сайта:
  раскрыто с подписями или свёрнуто до значков; коралловая «таблетка» перетекает между пунктами
  и растягивается под подпись (R23). Тёмное в обеих темах, фон свой (`rail-surface`).

  Выделение держат главная, разделы и закреплённые приложения. ИИ и уведомления открываются
  поверх страницы — выделение на них не переезжает.

  Ручка сворачивания — на правой кромке, на высоте знака: меню складывается к заклёпке, как веер.
  Внизу одна строка — «я» (AccountMenu): заглушка — знак контуром, по нажатию окно аккаунта; тема
  переключается в нём.

  Ядро знака и выделение — один объект (R41). Уходишь с главной — ядро вытекает из знака каплей,
  стекает к пункту и растекается в выделение; в знаке остаётся тонкое белое кольцо. Возвращаешься —
  выделение стягивается в каплю и возвращается ядром. Между пунктами выделение просто перетекает.

  Внутри помощника (ObserverProvider home="core", spec-3) меню — дом ядра: отдаёт его провайдеру
  (useCoreHome), а ядро в знаке и выделение отвечают за помощника — тон состояния, ритм ожидания, жест;
  пока ядро у события, в знаке кольцо, на пункте контур. Без помощника меню ровно прежнее: знак новых
  функций не получает (решение Максима 21.10).
*/

interface MainMenuProps {
  expanded: boolean;
  onToggleExpand: () => void;
  /** Главная: знак Kaname — её кнопка; дома ядро дышит, выделения нет. */
  homeActive: boolean;
  onHome: () => void;
  /** Пункт с выделением: раздел или закреплённое приложение. */
  activeId: string | null;
  /** Разделы платформы (Marketplace). */
  sections: MainMenuItem[];
  /** Закреплённые приложения — одно нажатие вместо каталога. */
  pinned?: MainMenuItem[];
  /** ИИ, уведомления, настройки. */
  tools: MainMenuItem[];
  onSelect: (id: string) => void;
  /** Окраска выделения по состоянию системы (R38); без неё — бренд. */
  tone?: NavTone;
  /** «Я» внизу меню: строка аккаунта и окно по нажатию. */
  account?: AccountInfo;
  /** Веер знака раскрыт; при входе он раскрывается, когда ядро садится в дом. */
  brandOpen?: boolean;
  /** Рабочее место собирается после входа: пункты появляются по очереди (ShellEntrance). */
  entering?: boolean;
  /** Веер знака живёт: дышит в такт ядру и время от времени поворачивается на лепесток. */
  life?: MarkLife;
  /** Меню спрятано («Фокус»): помощнику дома не видно. */
  hidden?: boolean;
}

/** Когда начинают появляться пункты при сборке после входа и шаг между ними, мс. */
const ENTER_FROM_MS = 820;
const ENTER_STEP_MS = 45;

const ACTIVE = '[data-pill-target="true"]';

/*
  Перелёт ядра ↔ выделение: капля вытекает из знака и растекается в выделение на пункте, обратно —
  стягивается домой. Кадры и темп — в brand/coreFlight.ts (общие с переходом после входа).
*/
type CoreAt = 'home' | 'pill';

export function MainMenu({
  expanded,
  onToggleExpand,
  homeActive,
  onHome,
  activeId,
  sections,
  pinned = [],
  tools,
  onSelect,
  tone = 'brand',
  account,
  brandOpen = true,
  entering = false,
  life,
  hidden = false,
}: MainMenuProps) {
  const ref = useRef<HTMLElement>(null);
  const pillRef = useRef<HTMLSpanElement>(null);
  const menuId = useId();
  const { box, ready } = useSlidingBox(ref, ACTIVE, `${homeActive ? 'home' : activeId}:${expanded}`);
  let order = 0;
  const enterDelay = () => (entering ? ENTER_FROM_MS + ENTER_STEP_MS * order++ : undefined);

  // Где живёт ядро: в знаке (главная, вход ещё не закончился, выделять нечего) или выделением на пункте.
  const want: CoreAt = !homeActive && brandOpen && box.visible ? 'pill' : 'home';
  const [coreAt, setCoreAt] = useState<CoreAt>(want);
  const [flight, setFlight] = useState<CoreAt | null>(null);
  // Первый показ страницы — ядро сразу на своём месте; перелетает только при переходах.
  const [live, setLive] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setLive(true));
    return () => cancelAnimationFrame(raf);
  }, []);
  // Дом ядра для помощника; без провайдера core — ничего не делает.
  const home = useCoreHome({
    asideRef: ref,
    pillRef,
    coreAt,
    flight,
    box,
    settling: entering || !brandOpen,
    hidden,
    homeActive,
    tone,
    homeKey: `${homeActive ? 'home' : activeId}:${expanded}:${coreAt}:${hidden}`,
  });
  // Пока ядро у события или отдаётся ему, своего перелёта нет: дом сразу на новом месте, капля вернётся туда.
  if (want !== (flight ?? coreAt)) {
    if (live && !prefersReducedMotion() && !home.out) {
      setFlight(want);
    } else {
      setFlight(null);
      setCoreAt(want);
    }
  }

  // Полёт считается от места на старте: последний замер выделения — из рефа, не из зависимостей.
  const boxRef = useRef(box);
  const flightRef = useRef<Animation | null>(null);
  // «Замедлить ×4» на стенде помощника: перелёты ядра тоже вчетверо медленнее — видно швы передачи.
  const slowRef = useRef(home.slow);
  useLayoutEffect(() => {
    boxRef.current = box;
    slowRef.current = home.slow;
  });
  useLayoutEffect(() => {
    const pill = pillRef.current;
    const aside = ref.current;
    if (!pill || !aside) return;
    if (flight === null) {
      // Долетели: состояние уже на месте — полёт снимаем, переезды между пунктами снова плавные.
      const done = flightRef.current;
      if (!done) return;
      done.cancel();
      flightRef.current = null;
      const raf = requestAnimationFrame(() => delete pill.dataset.flight);
      return () => cancelAnimationFrame(raf);
    }
    const mark = aside.querySelector('[data-brand-home] svg');
    if (!mark) return;
    const a = aside.getBoundingClientRect();
    const m = mark.getBoundingClientRect();
    const core = {
      x: m.left - a.left + m.width / 2,
      y: m.top - a.top + m.height / 2,
      r: (CORE_R / (MARK_HALF * 2)) * m.width,
    };
    const b = boxRef.current;
    const running = flightRef.current;
    const from = running ? frameOf(pill) : flight === 'pill' ? drop(core) : pillFrame(b);
    running?.cancel();
    pill.dataset.flight = '';
    const anim = pill.animate(
      flight === 'pill' ? outFrames(from, core, b, !running) : inFrames(from, core, b, !running),
      { duration: flight === 'pill' ? FLIGHT_OUT_MS : FLIGHT_IN_MS, fill: 'forwards' },
    );
    if (slowRef.current) anim.playbackRate = 0.25;
    flightRef.current = anim;
    anim.onfinish = () => {
      setCoreAt(flight);
      setFlight(null);
    };
  }, [flight]);
  useEffect(() => () => flightRef.current?.cancel(), []);

  // Ядро набухает в знаке перед уходом — оно ещё там, даже если дом уже сменился (капля выйдет из знака).
  const coreHome = home.releasing === 'mark' || (coreAt === 'home' && flight === null && !home.out);
  // Помощник: тон — у ядра, когда оно дома в знаке (в разделе тон несёт выделение, кольцо в знаке белое).
  const { core } = home;
  const markTone = core && coreHome ? tone : 'brand';
  const pillHome = home.rest && coreAt === 'pill' && flight === null && box.visible;

  return (
    <aside
      ref={ref}
      id={menuId}
      data-expanded={expanded || undefined}
      data-slot={core ? 'observer' : undefined}
      data-shell-card="menu"
      aria-label="Главное меню"
      className={cn(
        'group/menu relative z-raised flex shrink-0 flex-col overflow-hidden rounded-lg text-rail-fg rail-surface',
        'transition-width',
        expanded ? 'w-60' : 'w-20',
      )}
    >
      <div className="relative z-raised flex shrink-0 items-center px-4 py-3">
        <BrandHome
          expanded={expanded}
          homeActive={homeActive}
          onHome={onHome}
          open={brandOpen}
          core={coreHome ? 'home' : 'away'}
          life={life}
          tone={markTone}
          rhythm={home.awaiting && coreHome ? 'await' : 'calm'}
          cue={home.markCue}
          observed={!!core}
          coreHome={home.rest && coreHome}
        />
        <EdgeGrip expanded={expanded} onToggle={onToggleExpand} controls={menuId} />
      </div>

      {/* Выделение — после знака и до пунктов: в полёте капля идёт поверх знака, но под подписями. */}
      <span
        ref={pillRef}
        aria-hidden
        data-ready={ready || undefined}
        data-tone={tone === 'brand' ? undefined : tone}
        data-core-home={pillHome || undefined}
        data-await={home.pillAwait || undefined}
        className="rail-pill pointer-events-none absolute z-raised rounded-md state-tone"
        style={{
          top: box.top,
          left: box.left,
          width: box.width,
          height: box.height,
          opacity: coreAt === 'pill' && flight === null && box.visible && !home.out ? 1 : 0,
        }}
      />

      <nav className="relative z-raised flex flex-1 flex-col gap-1 overflow-y-auto px-4 py-1">
        {sections.map((item) => (
          <Item
            key={item.id}
            item={item}
            expanded={expanded}
            active={item.id === activeId}
            onSelect={onSelect}
            enterDelay={enterDelay()}
          />
        ))}
        {pinned.length > 0 && (
          <>
            <Divider expanded={expanded} />
            {pinned.map((item) => (
              <Item
                key={item.id}
                item={item}
                expanded={expanded}
                active={item.id === activeId}
                onSelect={onSelect}
                enterDelay={enterDelay()}
              />
            ))}
          </>
        )}
        <Divider expanded={expanded} />
        {tools.map((item) => (
          <Item
            key={item.id}
            item={item}
            expanded={expanded}
            active={item.id === activeId}
            onSelect={onSelect}
            enterDelay={enterDelay()}
          />
        ))}
      </nav>

      {account && (
        <div className="relative z-raised flex shrink-0 flex-col border-t border-rail-line px-4 py-3">
          <AccountMenu account={account} expanded={expanded} enterDelay={enterDelay()} />
        </div>
      )}
    </aside>
  );
}

/*
  Знак Kaname — кнопка «Главная» и дом ядра (docs/brand-concept.md §5–6, §8). Нажатие ведёт на
  главную; на главной ядро дышит и вокруг знака мягкое свечение — коралловой «таблетки» тут нет.
  Ядро вышло выделением на пункт — в знаке тонкое белое кольцо. Долгое нажатие заново раскрывает
  веер: лепестки складываются за ядро и выходят снова. Пасхалка: три быстрых нажатия подряд —
  порыв ветра, веер раскручивается вокруг ядра (useGust).
  data-brand-home — точка, куда садится ядро после входа (ShellEntrance).
*/
const LONG_PRESS_MS = 600;

function BrandHome({
  expanded,
  homeActive,
  onHome,
  open: shown,
  core,
  life,
  tone,
  rhythm,
  cue,
  observed,
  coreHome,
}: {
  expanded: boolean;
  homeActive: boolean;
  onHome: () => void;
  open: boolean;
  /** Ядро дома (сфера) или вышло выделением на пункт (кольцо). */
  core: 'home' | 'away';
  life?: MarkLife;
  /** Помощник: тон ядра, ритм ожидания, отклик дома. */
  tone: NavTone;
  rhythm: MarkRhythm;
  cue?: MarkCue | null;
  /** Знак — дом помощника: свечение берёт тон ядра. */
  observed: boolean;
  /** Ядро в покое дома — метка data-core-home. */
  coreHome: boolean;
}) {
  const [open, setOpen] = useState(true);
  const timer = useRef<number | null>(null);
  const longPressed = useRef(false);
  const { gust, tap } = useGust();

  const replay = () => {
    longPressed.current = true;
    setOpen(false);
    window.setTimeout(() => setOpen(true), 520);
  };
  const cancel = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  };

  return (
    <button
      type="button"
      aria-label="На главную"
      aria-current={homeActive ? 'page' : undefined}
      onPointerDown={() => {
        longPressed.current = false;
        timer.current = window.setTimeout(replay, LONG_PRESS_MS);
      }}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onClick={(e) => {
        if (longPressed.current) {
          e.preventDefault();
          longPressed.current = false;
          return;
        }
        tap();
        onHome();
      }}
      className="group/brand flex h-12 w-full shrink-0 items-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
    >
      <span
        data-brand-home=""
        data-core-home={coreHome || undefined}
        className="relative grid size-12 shrink-0 place-items-center"
      >
        <span
          aria-hidden
          data-tone={observed && tone !== 'brand' ? tone : undefined}
          data-await={(observed && rhythm === 'await') || undefined}
          className={cn(
            'absolute inset-0 rounded-full transition-opacity duration-smooth brand-halo',
            observed && 'core-tone',
            homeActive && core === 'home' ? 'opacity-100' : 'opacity-0',
          )}
        />
        <KanameMark
          open={open && shown}
          core={core}
          breathing={homeActive}
          life={life}
          gust={gust}
          tone={tone}
          rhythm={rhythm}
          cue={cue}
          className="relative size-10 transition-transform duration-normal group-hover/brand:scale-105"
        />
      </span>
      <MenuLabel expanded={expanded} className="ml-3 text-rail-fg">
        <KanameWordmark />
      </MenuLabel>
    </button>
  );
}

function Divider({ expanded }: { expanded: boolean }) {
  return (
    <span
      aria-hidden
      className={cn('my-2 block h-px shrink-0 bg-rail-line', expanded ? 'w-full' : 'mx-auto w-8')}
    />
  );
}

function Item({
  item,
  expanded,
  active,
  onSelect,
  enterDelay,
}: {
  item: MainMenuItem;
  expanded: boolean;
  active: boolean;
  onSelect: (id: string) => void;
  /** Задержка появления при сборке после входа, мс. */
  enterDelay?: number;
}) {
  const Icon = item.icon;
  const ai = item.accent === 'ai';
  // Пункт — источник событий для помощника (nav.<id>): сюда капля встаёт в коридоре меню.
  const meta = useMemo(() => ({ role: 'source' as const, label: item.label, level: 0 }), [item.label]);
  const anchor = useObserverAnchor<HTMLButtonElement>(`nav.${item.id}`, meta);
  // Выделение обнимает весь пункт, когда меню раскрыто, и только значок — когда свёрнуто.
  const button = (
    <button
      ref={anchor}
      type="button"
      data-menu-item={item.id}
      aria-label={item.label}
      aria-current={active ? 'page' : undefined}
      onClick={() => onSelect(item.id)}
      data-pill-target={(active && expanded) || undefined}
      style={enterDelay === undefined ? undefined : { animationDelay: `${enterDelay}ms` }}
      className={cn(
        'relative flex h-12 w-full shrink-0 items-center rounded-md transition-interactive outline-none focus-visible:ring-2 focus-visible:ring-inset',
        enterDelay !== undefined && 'animate-enter-rise motion-reduce:animate-none',
        // На коралловом выделении кольцо фокуса белое: коралл по кораллу не виден.
        active ? 'focus-visible:ring-on-brand' : 'focus-visible:ring-ring',
        active
          ? 'text-on-brand'
          : ai
            ? 'text-ai-fg hover:bg-ai-soft'
            : 'text-rail-fg-muted hover:bg-rail-hover hover:text-rail-fg',
      )}
    >
      <span
        data-pill-target={(active && !expanded) || undefined}
        className="grid size-12 shrink-0 place-items-center"
      >
        <Icon className="size-5" />
      </span>
      <MenuLabel expanded={expanded} className="ml-1 text-sm font-medium">
        {item.label}
      </MenuLabel>
      <BadgeMark badge={item.badge} expanded={expanded} active={active} />
    </button>
  );
  if (expanded) return button;
  return (
    <Tooltip content={item.hint ?? item.label} side="right">
      {button}
    </Tooltip>
  );
}

function BadgeMark({
  badge,
  expanded,
  active,
}: {
  badge: MainMenuItem['badge'];
  expanded: boolean;
  active: boolean;
}) {
  if (badge === undefined || badge === 0) return null;
  if (badge === 'dot' || !expanded) {
    return (
      <span aria-hidden className="absolute top-2 right-2 size-2 rounded-full bg-brand ring-2 ring-rail" />
    );
  }
  return (
    <span
      className={cn(
        'mr-3 rounded-xs px-1.5 py-0.5 text-2xs leading-none font-semibold',
        active ? 'bg-rail-active text-on-brand' : 'bg-brand text-on-brand',
      )}
    >
      {badge > 99 ? '99+' : badge}
    </span>
  );
}

/*
  Ручка сворачивания на правой кромке меню, на высоте строки знака — одна точка в обоих состояниях,
  едет вместе с краем. В покое —
  волосок цвета кромки; курсор над меню — волосок светлеет; на самой ручке, с клавиатуры и на
  сенсорном экране — капсула со стрелкой. Стрелка одна (R23): доворачивается в такт складыванию.
  Нажимается полоса 24 px у кромки (по WCAG 2.5.8): на знаке она задевает только пустой угол квадрата.
  Клавиша «[» — в оболочке, которая собирает меню. Кольцо фокуса внутри: снаружи его обрезал бы
  overflow-hidden меню.
*/
function EdgeGrip({
  expanded,
  onToggle,
  controls,
}: {
  expanded: boolean;
  onToggle: () => void;
  controls: string;
}) {
  const label = expanded ? 'Свернуть меню' : 'Развернуть меню';
  return (
    <Tooltip content={`${label} · [`} side="right">
      <button
        type="button"
        onClick={onToggle}
        aria-label={label}
        aria-expanded={expanded}
        aria-controls={controls}
        aria-keyshortcuts="["
        className="group/grip absolute inset-y-3 right-0 z-raised grid w-6 place-items-center rounded-l-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
      >
        <span
          className={cn(
            'grid h-6 w-1 place-items-center rounded-full bg-rail-line transition-all duration-normal ease-snappy motion-reduce:transition-none',
            'group-hover/menu:not-group-hover/grip:not-group-focus-visible/grip:bg-rail-fg-muted',
            'group-hover/grip:h-8 group-hover/grip:w-3 group-hover/grip:bg-rail-active',
            'group-focus-visible/grip:h-8 group-focus-visible/grip:w-3 group-focus-visible/grip:bg-rail-active',
            'pointer-coarse:h-8 pointer-coarse:w-3 pointer-coarse:bg-rail-active',
            'group-active/grip:scale-95',
          )}
        >
          <span
            className={cn(
              'grid place-items-center transition-transform duration-slow ease-in-out motion-reduce:transition-none',
              !expanded && 'rotate-180',
            )}
          >
            <ChevronLeft className="size-3 text-rail-fg opacity-0 transition-opacity duration-fast group-hover/grip:opacity-100 group-focus-visible/grip:opacity-100 pointer-coarse:opacity-100" />
          </span>
        </span>
      </button>
    </Tooltip>
  );
}
